<template>
  <section class="statistics-card">
    <h3 class="card-title">
      服务文艺家会员统计
    </h3>
    <div class="member-content">
      <div
        ref="chartRef"
        class="member-chart"
      />
      <aside class="member-summary">
        <div
          v-for="item in summaryItems"
          :key="item.label"
          class="summary-item"
          :style="{ '--summary-color': item.color }"
        >
          <div class="summary-value-row">
            <strong>{{ item.value }}</strong>
            <span>个</span>
            <span
              v-if="item.rate"
              class="summary-rate"
            >占比 {{ item.rate }}</span>
          </div>
          <p>{{ item.label }}</p>
        </div>
      </aside>
    </div>
  </section>
</template>

<script setup>
import { computed, markRaw, nextTick, onBeforeUnmount, onMounted, reactive, useTemplateRef } from 'vue'
import * as echarts from 'echarts'
import { getArtistMemberStatistics } from '@/api/primaryChannelStatistics'
import { ChannelIdentityTypeEnum } from '@/enums/ChannelTypeEnum'

const MEMBER_DEFINITIONS = [
  { code: ChannelIdentityTypeEnum.CITY_ASSOC.code, name: '直属事业单位\n及市级文艺家协会' },
  { code: ChannelIdentityTypeEnum.DISTRICT_ASSOC.code, name: '区级文艺家协会' },
  { code: ChannelIdentityTypeEnum.ART_NEW_ORG.code, name: '文艺两新组织' },
  { code: ChannelIdentityTypeEnum.SUPPORT_REGION.code, name: '对口帮扶地区组织' }
]

const statistics = reactive({
  total: 0,
  personalTotal: 0,
  enterpriseTotal: 0,
  personalPercentage: '0.00%',
  enterprisePercentage: '0.00%',
  items: buildDefaultItems()
})
const summaryItems = computed(() => [
  { label: '会员总数', value: statistics.total, rate: '', color: '#e5e7eb' },
  {
    label: '个人会员总数',
    value: statistics.personalTotal,
    rate: statistics.personalPercentage,
    color: '#2CB574'
  },
  {
    label: '企业会员总数',
    value: statistics.enterpriseTotal,
    rate: statistics.enterprisePercentage,
    color: '#FFBE36'
  }
])

const chartRef = useTemplateRef('chartRef')
let chart = null
let resizeObserver = null
let resizeFrame = 0

function buildDefaultItems() {
  return MEMBER_DEFINITIONS.map(item => ({
    ...item,
    personalTotal: 0,
    enterpriseTotal: 0,
    total: 0
  }))
}

function buildOption() {
  const mobile = (chart?.getWidth() || 0) <= 560
  return {
    color: ['#2CB574', '#FFBE36'],
    tooltip: {
      trigger: 'axis',
      confine: true,
      axisPointer: {
        type: 'shadow',
        shadowStyle: { color: 'rgba(239, 242, 247, 0.7)' }
      },
      formatter: (params) => {
        const total = params.reduce((sum, item) => sum + Number(item.value || 0), 0)
        const rows = params.map(item => (
          `<div style="margin-top:10px">${item.marker}${item.seriesName}：${item.value} 个</div>`
        )).join('')
        return `<strong>${String(params[0]?.axisValue || '').replace('\n', '')}会员：${total} 个</strong>${rows}`
      }
    },
    legend: {
      bottom: 0,
      icon: 'circle',
      itemWidth: 11,
      itemHeight: 11,
      itemGap: 40,
      textStyle: { color: '#444', fontSize: 13 },
      data: ['个人会员', '企业会员']
    },
    grid: {
      left: mobile ? 4 : 18,
      right: mobile ? 4 : 16,
      top: 18,
      bottom: mobile ? 86 : 64,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: statistics.items.map(item => item.name),
      axisTick: { show: false },
      axisLine: { lineStyle: { color: '#ddd' } },
      axisLabel: {
        interval: 0,
        color: '#333',
        fontSize: mobile ? 10 : 12,
        lineHeight: 16,
        margin: 14
      }
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: ({ max }) => max > 0 ? Math.ceil(max * 1.2) : 1,
      minInterval: 1,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: '#999', fontSize: 12 },
      splitLine: {
        lineStyle: {
          color: '#e5e7eb',
          type: 'dashed'
        }
      }
    },
    series: [
      {
        name: '个人会员',
        type: 'bar',
        barWidth: mobile ? 22 : 38,
        itemStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: '#2CB574' },
            { offset: 1, color: '#64D7A3' }
          ])
        },
        data: statistics.items.map(item => item.personalTotal)
      },
      {
        name: '企业会员',
        type: 'bar',
        barWidth: mobile ? 22 : 38,
        barGap: '0%',
        barCategoryGap: '42%',
        itemStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: '#FFB921' },
            { offset: 1, color: '#FFD66F' }
          ])
        },
        data: statistics.items.map(item => item.enterpriseTotal)
      }
    ]
  }
}

function renderChart() {
  chart?.setOption(buildOption(), { notMerge: true })
}

function resetStatistics() {
  statistics.total = 0
  statistics.personalTotal = 0
  statistics.enterpriseTotal = 0
  statistics.personalPercentage = '0.00%'
  statistics.enterprisePercentage = '0.00%'
  statistics.items = buildDefaultItems()
}

async function loadStatistics() {
  try {
    const response = await getArtistMemberStatistics()
    if (!response || response.code !== 200) {
      resetStatistics()
      return
    }
    const payload = response.data || {}
    const sourceItems = Array.isArray(payload.items) ? payload.items : []
    statistics.total = Number(payload.total) || 0
    statistics.personalTotal = Number(payload.personalTotal) || 0
    statistics.enterpriseTotal = Number(payload.enterpriseTotal) || 0
    statistics.personalPercentage = payload.personalPercentage || '0.00%'
    statistics.enterprisePercentage = payload.enterprisePercentage || '0.00%'
    statistics.items = MEMBER_DEFINITIONS.map((definition) => {
      const source = sourceItems.find(item => item.code === definition.code) || {}
      return {
        ...definition,
        personalTotal: Number(source.personalTotal) || 0,
        enterpriseTotal: Number(source.enterpriseTotal) || 0,
        total: Number(source.total) || 0
      }
    })
  } catch (error) {
    console.error('查询一级渠道服务文艺家会员统计失败:', error)
    resetStatistics()
  } finally {
    renderChart()
  }
}

onMounted(async () => {
  await nextTick()
  if (!chartRef.value) return
  chart = markRaw(echarts.init(chartRef.value))
  renderChart()
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      if (resizeFrame) cancelAnimationFrame(resizeFrame)
      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = 0
        chart?.resize()
        renderChart()
      })
    })
    resizeObserver.observe(chartRef.value)
  }
  loadStatistics()
})

onBeforeUnmount(() => {
  if (resizeFrame) cancelAnimationFrame(resizeFrame)
  resizeObserver?.disconnect()
  chart?.dispose()
})
</script>

<style lang="scss" scoped>
.statistics-card {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 22px 24px 18px;
  border: 1px solid #ebeef5;
  border-radius: 2px;
  background: #fff;
}

.card-title {
  margin: 0;
  color: #333;
  font-size: 18px;
  font-weight: bold;
  line-height: 1.4;
}

.member-content {
  display: grid;
  min-width: 0;
  grid-template-columns: minmax(0, 3.4fr) minmax(240px, 1fr);
  align-items: center;
  gap: 20px;
}

.member-chart {
  width: 100%;
  min-width: 0;
  height: 420px;
}

.member-summary {
  display: flex;
  min-height: 300px;
  padding-right: 12px;
  flex-direction: column;
  justify-content: space-around;
  gap: 38px;
}

.summary-item {
  position: relative;
  padding-left: 22px;

  &::before {
    position: absolute;
    top: 2px;
    bottom: 2px;
    left: 0;
    width: 4px;
    background: var(--summary-color);
    content: "";
  }

  p {
    margin: 7px 0 0;
    color: #666;
    font-size: 13px;
  }
}

.summary-value-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
  white-space: nowrap;

  strong {
    color: #111;
    font-size: 36px;
    line-height: 1;
  }

  span {
    color: #333;
    font-size: 14px;
  }

  .summary-rate {
    margin-left: 4px;
    color: #555;
  }
}

@media (max-width: 900px) {
  .member-content {
    grid-template-columns: minmax(0, 1fr) 190px;
    gap: 12px;
  }

  .member-summary {
    padding-right: 0;
  }

  .summary-value-row strong {
    font-size: 28px;
  }
}

@media (max-width: 768px) {
  .statistics-card {
    padding: 16px;
  }

  .card-title {
    font-size: 16px;
  }

  .member-content {
    grid-template-columns: 1fr;
  }

  .member-chart {
    height: 380px;
  }

  .member-summary {
    display: grid;
    min-height: 0;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
  }

  .summary-value-row {
    flex-wrap: wrap;

    strong {
      font-size: 24px;
    }
  }
}

@media (max-width: 480px) {
  .statistics-card {
    padding: 12px;
  }

  .card-title {
    font-size: 15px;
  }

  .member-chart {
    height: 360px;
  }

  .member-summary {
    grid-template-columns: 1fr;
    gap: 18px;
  }
}
</style>
